import { Dispatch, FC, SetStateAction, createContext, useContext, useMemo, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import { AddPartnerApplication } from '@prisma/client';
import { useSessionStorage } from 'react-use';

import { navigate } from '@redwoodjs/router';

import { Step } from 'src/components/StepperComponent/StepperComponent';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ADD_PARTNER_STEPS } from 'src/constants/partners';
import { ADD_PARTNER } from 'src/graphql/mutations/addPartner.mutation';
import {
    DELETE_PARTNER_APPLICATION,
    SUBMIT_PARTNER_APPLICATION,
} from 'src/graphql/mutations/partnerApplication';
import { ACCOUNT_MANAGERS } from 'src/graphql/queries/accountManagers.query';
import { FIND_OPEN_APPLICATION } from 'src/graphql/queries/findOpenApplication.query';
import { AddPartnerInput } from 'types/graphql';

// TODO: add type generator for typescript graphql
type Options = {
    label: string;
    value: string;
};

type AddPartnerProps = {
    queryPartnerApplication: () => Promise<void>;
    addPartner: (input: AddPartnerInput, currentActiveStep: number) => Promise<void>;
    application: AddPartnerApplication;
    submitPartnerApplication: () => Promise<void>;
    deletePartnerApplication: () => Promise<void>;
    accountManagerOptions: Options[];
    getAccountManagers: () => void;
    setApplication: Dispatch<SetStateAction<AddPartnerApplication>>;
    findingDraftPartnerApplication: boolean;
    draftAvailable: boolean;
    activeStep: number;
    steps: Step[];
    setActiveStep: Dispatch<SetStateAction<number>>;
    setSteps: Dispatch<SetStateAction<Step[]>>;
    handleBack: () => void;
};

const AddPartnerContext = createContext<Partial<AddPartnerProps>>({});

export const useAddPartnerContext = () => {
    return useContext(AddPartnerContext);
};

export const AddPartnerProvider: FC = ({ children }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState<Step[]>(ADD_PARTNER_STEPS);
    const [application, setApplication] =
        useSessionStorage<AddPartnerApplication>('ADD_PARTNER_APPLICATION');

    const [draftAvailable, setDraftAvailable] = useState<boolean>(false);

    const [accountManagerOptions, setAccountManagerOptions] = useState<Options[]>([]);

    const [addPartnerMutation, { loading: updatingPartnerApplication }] = useMutation(ADD_PARTNER, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: (data) => {
            setApplication(data?.addPartner);
        },
    });

    const [queryOpenApplication, { loading: findingDraftPartnerApplication }] = useLazyQuery(
        FIND_OPEN_APPLICATION,
        {
            fetchPolicy: 'network-only',
            onError: (error) => {
                return showErrorToast(error.message);
            },
            onCompleted: (data) => {
                if (data?.findOpenApplication) {
                    setApplication(data.findOpenApplication);
                    setDraftAvailable(true);
                } else {
                    setApplication(null);
                    setDraftAvailable(false);
                }
            },
        }
    );

    const [accountManagersQuery] = useLazyQuery(ACCOUNT_MANAGERS, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
        onCompleted: (data) => {
            setAccountManagerOptions(
                data?.accountManagers?.map(({ id, firstName, lastName }) => {
                    return {
                        value: id.toString(),
                        label: `${firstName} ${lastName}`,
                    };
                })
            );
        },
    });

    const queryPartnerApplication = async () => {
        try {
            await queryOpenApplication();
        } catch (error) {
            showErrorToast(error.message);
        }
    };

    const handleNext = (currentActiveStep: number) => {
        const allSteps = steps;
        allSteps[currentActiveStep].completed = true;
        // set the active step and set the completed prop on the list
        setSteps(allSteps);
        setActiveStep(currentActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => {
            return prevActiveStep - 1;
        });
    };

    // 'Next' button form submit handler
    const addPartner = async (input: AddPartnerInput, currentActiveStep: number) => {
        await addPartnerMutation({
            variables: { input: { applicationId: application?.id, ...input } },
        });

        if (!updatingPartnerApplication) {
            handleNext(currentActiveStep);
        }
    };

    const getAccountManagers = () => {
        accountManagersQuery();
    };

    const [submitPartnerApplicationMutation] = useMutation(SUBMIT_PARTNER_APPLICATION, {
        onError: (error) => {
            showErrorToast(error.message);
            if (
                error.message.includes(
                    'Please create the accounting connection manually from the partner details page'
                )
            ) {
                navigate('/partners');
                window.location.reload();
            }
        },
        onCompleted: () => {
            navigate('/partners');
            window.location.reload();
            showSuccessToast('Partner application submitted successfully');
        },
    });

    const [deletePartnerApplicationMutation] = useMutation(DELETE_PARTNER_APPLICATION, {
        onError: (error) => {
            return showErrorToast(error.message);
        },
    });

    const submitPartnerApplication = async () => {
        await submitPartnerApplicationMutation({
            variables: { applicationId: application?.id },
        });
    };

    const deletePartnerApplication = async () => {
        if (!application?.id) return;

        await deletePartnerApplicationMutation({
            variables: { applicationId: application?.id },
        });
    };

    const initialData = useMemo(() => {
        return {
            queryPartnerApplication,
            addPartner,
            submitPartnerApplication,
            deletePartnerApplication,
            application,
            accountManagerOptions,
            getAccountManagers,
            setApplication,
            findingDraftPartnerApplication,
            draftAvailable,
        };
    }, [application, accountManagerOptions]);

    return (
        <AddPartnerContext.Provider
            value={{
                ...initialData,
                activeStep,
                steps,
                setActiveStep,
                setSteps,
                handleBack,
            }}
        >
            {children}
        </AddPartnerContext.Provider>
    );
};
